import { useNavigate, useParams, Link } from "react-router-dom";
import { LoadingButton, Popconfirm, Spinner, Stack } from "@altimateai/lego";
import {
  Card,
  CardContent,
  Button,
  Input,
  Typography,
  toast,
  Badge,
  Label,
  Alert,
  AlertDescription,
  AlertTitle,
} from "@altimateai/lego";
import { Privacy } from "@/helpers/constants";
import { useCanEditPrivacyAware } from "@/helpers/privacyPermissions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteKnowledgeSource,
  getKnowledgeBaseTemplates,
  getKnowledgeSourceById,
  getKnowledgeBasePrivateLinkSetting,
} from "@/helpers/apis";
import { MdxMarkdownEditor } from "@/components/markdownEditor";
import { formatDate, parseId } from "@/helpers/utils";
import { CopyButton } from "@/components/copyButton";
import { API_URL } from "@/config";
import { getTenant } from "@/helpers/tenant";
import { statusColors } from "./constants";
import { useIntercom } from "react-use-intercom";
import PrivacyAndCreatorInfo from "@/components/PrivacyAndCreatorInfo";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { KnowledgeTracking } from "@/lib/tracking/knowledge";

export default function KnowledgeCatalogDetailsV1() {
  const { id, templateName } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useIntercom();

  const {
    data: knowledgeSource,
    isFetching: isKnowledgeSourceLoading,
    error: knowledgeSourceError,
  } = useQuery({
    queryKey: ["knowledgeSource", id],
    queryFn: () => getKnowledgeSourceById(id!),
    enabled: !!id,
  });

  const { data: knowledgeTemplates, isFetching: isTemplatesLoading } = useQuery({
    queryKey: ["knowledgeTemplates"],
    queryFn: () => getKnowledgeBaseTemplates(),
  });

  const { data: privateLinkSetting } = useQuery({
    queryKey: ["knowledgeBasePrivateLinkSetting"],
    queryFn: () => getKnowledgeBasePrivateLinkSetting(),
  });

  const template = knowledgeTemplates?.find(template => template.name === templateName);

  const data = templateName ? template : knowledgeSource;
  const { mutate, isPending: isDeleting } = useMutation({
    mutationFn: () => {
      const parsedId = parseId(id);
      if (!parsedId) {
        throw new Error("Invalid knowledge base id");
      }
      return deleteKnowledgeSource(parsedId);
    },
    onSuccess: () => {
      if (id) {
        KnowledgeTracking.sourceDeleted(id);
      }
      queryClient.invalidateQueries({ queryKey: ["knowledgeSource", id] });
      navigate("/knowledge-hub");
    },
    onError: () => {
      toast({
        title: "Failed to delete knowledge",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const canEditKnowledgeSource = useCanEditPrivacyAware();
  const createdDate = data?.created_at ? formatDate(data.created_at) : "N/A";
  const lastUpdatedDate = data?.updated_at ? formatDate(data.updated_at) : "N/A";
  const isPrivateLinkEnabled = privateLinkSetting?.config?.enabled;
  const port = privateLinkSetting?.config?.port;
  const link = isPrivateLinkEnabled
    ? `http://localhost:${port ?? 4000}/kb/${data?.public_id}`
    : `${API_URL}/knowledge_bases/public/${data?.public_id}?tenant=${getTenant()}`;
  const title = data?.name;
  const handleRequestPrivateLink = () => {
    show();
  };
  const handleLinkCopied = () => {
    if (id) {
      KnowledgeTracking.linkCopied(id);
    }
  };

  useBreadcrumb(title ?? "Loading...", {
    defaultParents: [
      { label: "Datamates", pathname: "/datamates" },
      { label: "Knowledge Hub", pathname: "/knowledge-hub" },
    ],
  });

  return (
    <Stack className="al-p-6 al-gap-6" direction="column">
      {isKnowledgeSourceLoading || isTemplatesLoading ? (
        <Spinner />
      ) : knowledgeSourceError && !!id ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load knowledge source</AlertTitle>
          <AlertDescription>
            {(knowledgeSourceError as Error)?.message ||
              "There was an error loading the knowledge source. Please try again later."}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <Stack direction="row" justify="between" align="center">
            <Typography variant="page-heading">{title}</Typography>
            {!templateName && data && canEditKnowledgeSource(data) ? (
              <Stack>
                <Button asChild variant="outline">
                  <Link to={`/knowledge-hub/edit/${id}`}>Edit</Link>
                </Button>
                <Popconfirm
                  title="Are you sure?"
                  description="This action cannot be undone."
                  onConfirm={() => mutate()}
                >
                  <LoadingButton loading={isDeleting} variant="destructive">
                    Delete
                  </LoadingButton>
                </Popconfirm>
              </Stack>
            ) : null}
          </Stack>
          {data?.user_id && (
            <>
              <PrivacyAndCreatorInfo
                privacy={data.privacy || Privacy.PRIVATE}
                userId={data.user_id}
              />
            </>
          )}
          {!templateName ? (
            <>
              <Stack direction="row" justify="start" align="center" className="al-gap-4">
                {data?.forked && (
                  <Badge variant="outline" className={statusColors["Forked"]}>
                    Forked
                  </Badge>
                )}
                <div>Created: {createdDate}</div>
                <div>Last Updated: {lastUpdatedDate}</div>
              </Stack>
              <Stack direction="column">
                <Stack direction="row" align="center" className="al-max-w-md">
                  <Label>
                    {" "}
                    Link: <Input value={link} readOnly />
                  </Label>
                  <span onClick={handleLinkCopied} role="presentation">
                    <CopyButton value={link} />
                  </span>
                </Stack>
                <p className="al-text-sm al-text-muted-foreground">
                  You can use this knowledge source and add it as a @DOC reference in CursorAI or
                  use it directly as a link in your IDE of choice.{" "}
                  <Button variant="link" asChild>
                    <a
                      href="https://datamates-docs.myaltimate.com/user-guide/components/knowledgehub/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Read more.
                    </a>
                  </Button>
                  <br />
                  {isPrivateLinkEnabled ? (
                    <p className="al-text-success">
                      This knowledge document is protected with private link access.
                    </p>
                  ) : (
                    <p className="al-text-destructive">
                      This knowledge document will be accessible to anyone with the link.
                      <Button variant="link" onClick={handleRequestPrivateLink}>
                        Request private link
                      </Button>
                    </p>
                  )}
                </p>
              </Stack>
            </>
          ) : null}
          <Card role="article">
            <CardContent className="al-pt-4">
              {data?.content ? (
                <MdxMarkdownEditor markdown={data.content.pages[0].content} readOnly />
              ) : (
                <p>No context available.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
